import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, Loader2, Upload } from 'lucide-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ContactSupport from './components/ContactSupport';
import PrivacyRefund from './components/PrivacyRefund';
import Maintenance from './components/Maintenance';
import Header from './components/Header';
import PricingPage from './components/PricingPage';
import InstructionsPage from './components/InstructionsPage';
import AdminDashboard from './components/AdminDashboard';
import QuickBooksEditor from './components/QuickBooksEditor';
import CoupaEditor from './components/CoupaEditor';
import CreditModal from './components/CreditModal';
import LoginScreen from './components/LoginScreen';

import QuickBooksBatchPage from './components/QuickBooksBatchPage';
import CoupaBatchPage from './components/CoupaBatchPage';
import TargetSelectionPage from './components/TargetSelectionPage';
import ConfigurationPage from './components/ConfigurationPage';
import { handleGoogleLogin as googleLogin, handleLogout as logout } from './utils/authHelpers';

import { auth, db } from "./firebase";
import { 
  onAuthStateChanged
} from "firebase/auth";
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  onSnapshot,
  collection,
  getDocs,
  addDoc,
} from "firebase/firestore";

/* eslint-disable react-hooks/exhaustive-deps */
const InvoiceProcessor = () => {
  const [user, setUser] = useState(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      return {
        id: currentUser.uid,
        name: currentUser.displayName || 'User',
        email: currentUser.email,
        picture: currentUser.photoURL || '',
        isNewUser: false,
        isAdmin: false
      };
    }
    return null;
  });

  const [activeTab, setActiveTab] = useState('invoice');
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const [credits, setCredits] = useState(0);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [exportFormat, setExportFormat] = useState('csv');
  const [targetSystem, setTargetSystem] = useState('quickbooks');
  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);
  const [quickbooksBatchData, setQuickbooksBatchData] = useState([]);
  const [coupaBatchData, setCoupaBatchData] = useState([]);
  // NEW STATES FOR TARGET SELECTION
  const [showTargetSelection, setShowTargetSelection] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);

  const fileInputRef = useRef(null);
  const [invoiceData, setInvoiceData] = useState({
    'invoice-date': '',
    'invoice-number': '',
    'due-date': '',
    currency: { code: 'USD' },
    supplier: { 
      number: '',
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      email: '',
      phone: ''
    },
    customer: {
      number: '',
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      email: '',
      phone: ''
    },
    'supplier-site': { code: '' },
    'remit-to-address': { 'remit-to-code': '' },
    'ship-to-address': { id: '', 'country-code': '' },
    'requester-email': '',
    'line-level-taxation': '',
    'account-type': { name: '' },
    'payment-terms': '',
    "handling-amount": "",
    "shipping-amount": "",
    "misc-amount": "",
    "discount-amount": "",
    'default-account-code': '',
    'invoice-lines': [],
    'tax-lines': [],
    'custom-fields': { 'invoice-total': '' }
  });

  useEffect(() => {
    let unsubscribeCredits = null;
    
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('User authenticated:', firebaseUser.email);
        const userRef = doc(db, 'users', firebaseUser.uid);
        
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          console.log('New user detected, creating Firestore document...');
          
          const newUserData = {
            email: firebaseUser.email,
            name: firebaseUser.displayName || 'User',
            credits: 10,
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            photoURL: firebaseUser.photoURL || '',
            isAdmin: false,
            invoicesProcessed: 0,
            targetSystem: null  // NEW - no target selected yet
          };

          try {
            await setDoc(userRef, newUserData);
            console.log('✅ New user created in Firestore with 10 credits');
            setShowTargetSelection(true); // SHOW TARGET SELECTION PAGE
          } catch (error) {
            console.error('❌ Error creating user:', error);
          }
        } else {
          // CHECK IF USER HAS TARGET SYSTEM
          const userData = userDoc.data();
          if (!userData.targetSystem) {
            setShowTargetSelection(true); // SHOW TARGET SELECTION
          } else {
            setTargetSystem(userData.targetSystem); // SET TARGET FROM FIREBASE
            setShowTargetSelection(false);
          }
        }
        
        unsubscribeCredits = onSnapshot(userRef, (userSnap) => {
          if (userSnap.exists()) {
            const data = userSnap.data();
            const userCredits = data.credits ?? 0;
            const isAdmin = data.isAdmin || false;
            const userTarget = data.targetSystem;
            
            console.log('User data updated:', { credits: userCredits, isAdmin, targetSystem: userTarget });
            setCredits(userCredits);
            
            if (userTarget) {
              setTargetSystem(userTarget);
            }
            
            setUser({
              id: firebaseUser.uid,
              name: firebaseUser.displayName || 'User',
              email: firebaseUser.email,
              picture: firebaseUser.photoURL || '',
              isNewUser: false,
              isAdmin: isAdmin
            });
          }
        }, (error) => {
          console.error('❌ Error listening to user data:', error);
          setCredits(0);
        });
        
      } else {
        console.log('User signed out');
        setUser(null);
        setCredits(0);
        setShowTargetSelection(false);
        if (unsubscribeCredits) {
          unsubscribeCredits();
          unsubscribeCredits = null;
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeCredits) {
        unsubscribeCredits();
      }
    };
  }, []);

  useEffect(() => {
    console.log('Admin useEffect triggered', { isAdmin: user?.isAdmin, activeTab });
    
    if (!user?.isAdmin || activeTab !== 'admin') {
      console.log('Not fetching - conditions not met');
      return;
    }

    const fetchAdminData = async () => {
      try {
        console.log('Fetching users from Firebase...');
        const usersSnap = await getDocs(collection(db, 'users'));
        console.log('Users snapshot size:', usersSnap.size);
        
        const users = usersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Fetched users:', users);
        setAllUsers(users);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchAdminData();
  }, [user, activeTab]);

  const logTransaction = async (invoiceNumber, fileName) => {
    if (!user) return;
    
    try {
      await addDoc(collection(db, 'transactions'), {
        userId: user.id,
        userEmail: user.email,
        invoiceNumber: invoiceNumber || 'N/A',
        fileName: fileName || 'unknown.pdf',
        timestamp: serverTimestamp(),
        status: 'success'
      });
      console.log('✅ Transaction logged');
    } catch (error) {
      console.error('❌ Error logging transaction:', error);
    }
  };

  // NEW FUNCTION - Save target to Firebase
  const handleSelectTarget = async (selectedTarget) => {
    if (!user) return;
    
    setSavingTarget(true);
    
    try {
      const userRef = doc(db, 'users', user.id);
      await setDoc(userRef, { 
        targetSystem: selectedTarget 
      }, { merge: true });
      
      console.log('✅ Target system saved:', selectedTarget);
      setTargetSystem(selectedTarget);
      setShowTargetSelection(false);
      setSavingTarget(false);
    } catch (error) {
      console.error('❌ Error saving target:', error);
      alert('Failed to save target system. Please try again.');
      setSavingTarget(false);
    }
  };

  const handleGoogleLoginWrapper = () => {
    googleLogin(setLoading, setProcessingError);
  };

  const handleLogoutWrapper = () => {
    logout(setUser, setCredits, setPdfFile, setPdfUrl, setActiveTab, setInvoiceData);
  };

  const handlePurchaseCredits = async (credits, amount) => {
    if (!user) {
      alert('Please sign in first');
      return;
    }

    setIsLoadingCheckout(true);

    try {
      const token = await auth.currentUser.getIdToken();

      console.log('🎯 Starting payment:', { credits, amount });

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          credits: credits,
          amount: amount,
        }),
      });

      console.log('📥 Response status:', response.status);

      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Server returned non-JSON response:', text.substring(0, 200));
        throw new Error('Server configuration error. Please check console.');
      }

      const result = await response.json();
      console.log('✅ Response:', result);

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (!result.success || !result.url) {
        throw new Error(result.error || 'No checkout URL received');
      }

      console.log('🚀 Redirecting to Stripe checkout:', result.url);
      window.location.href = result.url;
      
    } catch (error) {
      console.error('💥 Payment error:', error);
      alert(`Payment failed: ${error.message}`);
      setIsLoadingCheckout(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus === 'success') {
      alert('🎉 Payment successful! Your credits have been added.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      alert('Payment was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const extractInvoiceBackend = async (pdfBase64) => {
    try {
      const token = await auth.currentUser.getIdToken();
      
      const response = await fetch('/api/extract', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pdfBase64 })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Backend processing error');
      }
      return result.data;
    } catch (error) {
      console.error('Backend Extract Error:', error);
      throw error;
    }
  };

  const clearQuickbooksBatch = () => {
  // Revoke all PDF URLs
  quickbooksBatchData.forEach(item => {
    if (item.pdfUrl) {
      URL.revokeObjectURL(item.pdfUrl);
    }
  });
  setQuickbooksBatchData([]);
  setBatchFiles([]);
  setBatchMode(false);
};
  const clearCoupaBatch = () => {
  // Revoke all PDF URLs
  coupaBatchData.forEach(item => {
    if (item.pdfUrl) {
      URL.revokeObjectURL(item.pdfUrl);
    }
  });
  setCoupaBatchData([]);
  setBatchFiles([]);
  setBatchMode(false);
};

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (!files || files.length === 0) {
      setProcessingError('Please upload at least one PDF file');
      return;
    }

    if (files.length > 15) {
      setProcessingError('Maximum 15 files allowed per batch. Please select fewer files.');
      e.target.value = '';
      return;
    }

    if (credits < files.length) {
    setProcessingError(`Insufficient credits. You need ${files.length} credits but only have ${credits}. Please purchase more credits or select fewer files.`);
    setShowCreditModal(true);
    e.target.value = '';
    return;
    }

    for (const file of files) {
      if (file.type !== 'application/pdf') {
        setProcessingError('All files must be valid PDF files');
        e.target.value = '';
        return;
      }
      
      const MAX_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        setProcessingError(`File ${file.name} exceeds 10MB limit`);
        e.target.value = '';
        return;
      }
    }

    if (isProcessing) {
      setProcessingError('Please wait for current processing to finish');
      return;
    }

    setProcessingError(null);

    if (files.length > 1) {
      setBatchMode(true);
      setBatchFiles(files);
      setQuickbooksBatchData([]); // Clear previous batch data
       setCoupaBatchData([]);
    } else {
      setBatchMode(false);
      const file = files[0];
      setPdfFile(file);
      const url = URL.createObjectURL(file);
      setPdfUrl(url);

      setIsProcessing(true);

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const base64 = event.target.result.split(',')[1];
            const extractedData = await extractInvoiceBackend(base64);
            setInvoiceData(extractedData);
            setIsProcessing(false);
            
            if (extractedData['invoice-number']) {
              await logTransaction(extractedData['invoice-number'], file.name);
            }
          } catch (error) {
            setProcessingError(error.message);
            setIsProcessing(false);
            
            if (error.message?.includes('Insufficient credits') || 
                error.message?.includes('credit')) {
              setShowCreditModal(true);
            }
          }
        };
        reader.onerror = () => {
          setProcessingError('Failed to read PDF file');
          setIsProcessing(false);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        setProcessingError(error.message);
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      
      {/* ========== NEW: TARGET SELECTION CHECK ========== */}
      {showTargetSelection && user ? (
        <TargetSelectionPage 
          onSelectTarget={handleSelectTarget}
          saving={savingTarget}
        />
      ) : (
        <>
          {/* ========== NORMAL APP (shown when target is selected) ========== */}
          <Header 
            user={user} 
            credits={credits} 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            handleLogout={handleLogoutWrapper} 
            targetSystem={targetSystem} 
          />

          <div className="max-w-[1800px] mx-auto px-4 py-1">
            {activeTab === 'instructions' && (
              <InstructionsPage />
            )}

            {activeTab === 'pricing' && (
              <PricingPage 
                user={user}
                handleGoogleLogin={handleGoogleLoginWrapper}
                handlePurchaseCredits={handlePurchaseCredits}
                loading={loading}
                isLoadingCheckout={isLoadingCheckout}
              />
            )}

            {activeTab === 'admin' && user?.isAdmin && (
              <AdminDashboard 
                allUsers={allUsers} 
                setAllUsers={setAllUsers}
              />
            )}
{activeTab === 'configuration' && user && targetSystem === 'coupa' && (
  <ConfigurationPage user={user} />
)}
            {activeTab === 'invoice' && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  multiple
                />
                
                {processingError && (
                  <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Error: {processingError}</span>
                    </div>
                  </div>
                )}

                {batchMode ? (
  targetSystem === 'quickbooks' ? (
    <QuickBooksBatchPage
      files={batchFiles}
      batchData={quickbooksBatchData}
      setBatchData={setQuickbooksBatchData}
      user={user}
      extractInvoiceBackend={extractInvoiceBackend}
      logTransaction={logTransaction}
      onBack={() => {
  if (quickbooksBatchData.length === 0) {
    // If batch is empty, clear everything and go to upload
    setBatchMode(false);
    setBatchFiles([]);
    setQuickbooksBatchData([]);
  } else if (window.confirm('Clear batch data and return to main screen?')) {
    clearQuickbooksBatch();
  } else {
    setBatchMode(false);
  }
}}
    />
  ) : (
  <CoupaBatchPage
    files={batchFiles}
    batchData={coupaBatchData}
    setBatchData={setCoupaBatchData}
    user={user}
    extractInvoiceBackend={extractInvoiceBackend}
    logTransaction={logTransaction}
    exportFormat={exportFormat}
    setExportFormat={setExportFormat}
   onBack={() => {
  if (coupaBatchData.length === 0) {
    // If batch is empty, clear everything and go to upload
    setBatchMode(false);
    setBatchFiles([]);
    setCoupaBatchData([]);
  } else if (window.confirm('Clear batch data and return to main screen?')) {
    clearCoupaBatch();
  } else {
    setBatchMode(false);
  }
}}
  />
)
) : !pdfFile && !user ? (
                  <LoginScreen 
                    handleGoogleLogin={handleGoogleLoginWrapper}
                    loading={loading}
                    user={user}
                    fileInputRef={fileInputRef}
                    isProcessing={isProcessing}
                    credits={credits}
                  />
                ) : !pdfFile && user ? (
                  <div className="flex items-center justify-center h-[calc(100vh-180px)]">
                    <div className="text-center">
                      <div className="relative inline-block">
                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl blur-xl opacity-20 animate-pulse"></div>
                        <div className="relative bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
                          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center">
                            <Upload className="w-12 h-12 text-indigo-600" />
                          </div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-3">Upload Invoice PDF</h2>
                          <p className="text-gray-600 mb-8 max-w-md">
                            Upload your invoice to extract and structure data
                          </p>
                          <button
                            onClick={() => fileInputRef.current.click()}
                            disabled={isProcessing}
                            className="px-8 py-3 rounded-xl font-medium transition-all bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? 'Processing...' : 'Choose PDF Files (1 Credit Each, Max 15)'}
                          </button>
                          <p className="text-sm text-gray-600 mt-4">
                            You have <span className="font-bold text-indigo-600">{credits} credits</span> remaining
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-2">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-900">Invoice Preview</h3>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isProcessing}
                            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? 'Processing...' : 'Change File'}
                          </button>
                        </div>
                        {isProcessing ? (
                          <div className="flex flex-col items-center justify-center h-[calc(100vh-130px)]">
                            <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mb-4" />
                            <p className="text-gray-600 font-medium">AI is analyzing your invoice...</p>
                            <p className="text-sm text-gray-500 mt-2">This may take 10-30 seconds</p>
                          </div>
                        ) : pdfUrl ? (
                          <div className="bg-gray-100 rounded-lg h-[calc(100vh-130px)] overflow-hidden">
                            <iframe 
                              src={pdfUrl} 
                              className="w-full h-full rounded-lg border-0"
                              title="Invoice Preview"
                            />
                          </div>
                        ) : null}
                      </div>

                      <div className="space-y-2">
                        {targetSystem === 'quickbooks' ? (
                          <QuickBooksEditor
                            invoiceData={invoiceData}
                            setInvoiceData={setInvoiceData}
                            pdfFile={pdfFile}
                            showJson={showJson}
                            setShowJson={setShowJson}
                          />
                        ) : (
                          <CoupaEditor
                            invoiceData={invoiceData}
                            setInvoiceData={setInvoiceData}
                            pdfFile={pdfFile}
                            showJson={showJson}
                            setShowJson={setShowJson}
                            user={user}
                            exportFormat={exportFormat}
                            setExportFormat={setExportFormat}
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}

      {showCreditModal && (
        <CreditModal
          setShowCreditModal={setShowCreditModal}
          handlePurchaseCredits={handlePurchaseCredits}
          isLoadingCheckout={isLoadingCheckout}
        />
      )}

      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

function App() {
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAppReady(true);
    });
    return () => unsubscribe();
  }, []);

  const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  if (!isAppReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-700 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<InvoiceProcessor />} />
        <Route path="/contact" element={<ContactSupport />} />
        <Route path="/privacy" element={<PrivacyRefund />} />
      </Routes>

      <footer className="bg-gray-900 text-white py-2 mt-3">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-2 p-2 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="text-xs font-semibold text-gray-300 mb-1">⚖️ Legal Disclaimer</h3>
            <p className="text-[10px] text-gray-400 leading-tight mb-1.5">
              Independent tool. <strong>NOT affiliated with Coupa or Intuit.</strong> "Coupa" and "QuickBooks" are registered trademarks of their respective owners.
            </p>
            <p className="text-[10px] text-gray-400 leading-tight">
              <strong>No Warranty:</strong> Provided "as is" without warranty.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-2">
            <a href="/contact" className="hover:text-blue-400 text-xs" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Contact</a>
            <a href="/privacy" className="hover:text-blue-400 text-xs" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Privacy & Refund</a>
          </div>
          
          <p className="text-gray-400 text-xs text-center">© 2025 AP Invoice Processor</p>
        </div>
      </footer>
    </BrowserRouter>
  );
}

export default App;